import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { WarningAmber as WarningAmberIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { bulkTimelineUpdate } from '../services/api';

const fmtCurrency = (val) => {
  const num = parseFloat(val);
  if (isNaN(num) || num === 0) return '—';
  if (num >= 1_000_000) return '$' + (num / 1_000_000).toFixed(1) + 'M';
  if (num >= 1_000) return '$' + (num / 1_000).toFixed(0) + 'K';
  return '$' + num.toLocaleString();
};

/**
 * TimelineGapModal
 *
 * Props:
 *   gaps    — array of project objects from /api/projects/my-timeline-gaps
 *   onClose — called when the modal should close (either saved or deferred)
 */
const TimelineGapModal = ({ gaps, onClose }) => {
  // Map of project_id → date string entered by the user
  const [dates, setDates] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!gaps || gaps.length === 0) return null;

  const anyDateEntered = Object.values(dates).some((d) => d && d.trim() !== '');

  const handleDateChange = (projectId, value) => {
    setDates((prev) => ({ ...prev, [projectId]: value }));
  };

  const handleSave = async () => {
    const updates = Object.entries(dates)
      .filter(([, date]) => date && date.trim() !== '')
      .map(([project_id, projected_completion_date]) => ({
        project_id: parseInt(project_id, 10),
        projected_completion_date,
      }));

    if (updates.length === 0) return;

    setSaving(true);
    setError(null);
    try {
      await bulkTimelineUpdate(updates);
      onClose({ saved: true });
    } catch (err) {
      console.error('Bulk timeline update failed:', err);
      setError('Failed to save dates. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemindLater = () => {
    // Flag in sessionStorage so the modal won't reopen during this browser session
    try {
      sessionStorage.setItem('timeline_gap_deferred', '1');
    } catch (_) {}
    onClose({ saved: false });
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="lg"
      disableEscapeKeyDown
      // Prevent closing by clicking the backdrop
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose({ saved: false });
      }}
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" fontSize="large" />
          <Box>
            <Typography variant="h6" fontWeight="bold" component="span">
              Timeline Update Required
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              The following projects need expected completion dates before you continue.
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ m: 2, mb: 0 }}>
            {error}
          </Alert>
        )}

        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Job #</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Project Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Client</TableCell>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Contract Value</TableCell>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Current Phase</TableCell>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Your Role</TableCell>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', minWidth: 170 }}>
                Expected Completion *
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {gaps.map((project) => {
              const prefillDate = project.sage_est_comp
                ? project.sage_est_comp.substring(0, 10)
                : '';
              const currentVal = dates[project.id] !== undefined
                ? dates[project.id]
                : prefillDate;

              return (
                <TableRow
                  key={project.id}
                  hover
                  sx={{
                    bgcolor: currentVal ? 'success.50' : 'inherit',
                    transition: 'background-color 0.15s',
                  }}
                >
                  <TableCell>
                    <Typography
                      component={Link}
                      to={`/projects/${project.id}`}
                      variant="body2"
                      sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 'bold', '&:hover': { textDecoration: 'underline' } }}
                      target="_blank"
                      rel="noopener"
                    >
                      {project.primary_job_number || `#${project.id}`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {project.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {project.client_name || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {fmtCurrency(project.sage_contract_amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {project.current_phase ? (
                      <Chip
                        label={`Phase ${project.current_phase}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {project.owner_role ? (
                      <Chip
                        label={project.owner_role}
                        size="small"
                        color={project.owner_role === 'Both' ? 'secondary' : (project.owner_role === 'Sales' ? 'info' : 'default')}
                        variant={project.owner_role === 'Both' ? 'filled' : 'outlined'}
                      />
                    ) : (
                      <Typography variant="body2" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <input
                      type="date"
                      value={currentVal}
                      onChange={(e) => handleDateChange(project.id, e.target.value)}
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        fontSize: '0.875rem',
                        border: currentVal
                          ? '1.5px solid #2e7d32'
                          : '1.5px solid #ed6c02',
                        borderRadius: 4,
                        outline: 'none',
                        cursor: 'pointer',
                        background: currentVal ? '#f1f8f1' : '#fff8f0',
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Box sx={{ px: 3, py: 1.5, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            * Enter a date for at least one project to enable Save &amp; Continue. You can come back to fill in the rest.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          variant="text"
          color="inherit"
          onClick={handleRemindLater}
          disabled={saving}
          sx={{ mr: 'auto', color: 'text.secondary' }}
        >
          Remind Me Later
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleSave}
          disabled={!anyDateEntered || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <WarningAmberIcon />}
        >
          {saving ? 'Saving…' : 'Save & Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TimelineGapModal;
